import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { LibraryAdapter } from '@govu/shared'
import { firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'
import { createFirestoreLibraryAdapter } from './firestoreAdapter'

interface AdapterCtx {
  library: LibraryAdapter
}

const Ctx = createContext<AdapterCtx | null>(null)

/**
 * Wraps the entire PWA in a single LibraryAdapter that every consumer
 * (existing useLibrary* hooks, future shared components in
 * @govu/shared/ui) reads through useLibraryAdapter(). Today this is
 * always backed by Firestore; once the desktop adopts the same
 * pattern, the renderer there will plug in an IpcLibraryAdapter and
 * shared components will work in both contexts unchanged.
 */
export function DataAdapterProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  // The Firestore adapter holds a reference to the live uid via a
  // closure. Memoize so the adapter identity is stable across renders
  // when the uid hasn't changed — onSnapshot subscriptions in callers
  // would otherwise tear down + reattach on every render.
  const value = useMemo<AdapterCtx>(() => {
    const library = createFirestoreLibraryAdapter({
      firestore,
      getUid: () => user?.uid ?? null,
    })
    return { library }
  }, [user?.uid])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useLibraryAdapter(): LibraryAdapter {
  const ctx = useContext(Ctx)
  if (!ctx) {
    throw new Error('useLibraryAdapter must be used inside <DataAdapterProvider>')
  }
  return ctx.library
}
